import { closestCenter, DndContext } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { EmptyState, Sheet } from "../../design-v2";
import { MetricTile, UnsupportedBlockCard } from "../../components/product";
import {
  BlockEditorCardio,
  BlockEditorSingle,
  BlockEditorSuperset,
  BuilderShell,
  MuscleDistributionPanel,
  SaveStatusPill,
} from "../builders";
import ExerciseSearchPanel from "./ExerciseSearchPanel";
import SortableBlock, { getBlockUiKey, isSortableBlockType } from "./sortableBlock";

// PHASE 7A DESIGN V2 RENDERER.
//
// Presentation only. It receives the identical functional props object the V1
// rollback renderer receives (blueprint section 7, rule S2) and owns no state,
// no persistence, no autosave, no validation and no mutators.
//
// Exercise search (Phase 7B) is rendered through the same shared seam both
// branches use, asking it for its Design V2 presentation. Only the presentation
// differs by branch: the search behaviour, callbacks and state ownership arrive
// from the container and are identical on both (blueprint D23 / section 4.1).
export default function WorkoutBuilderRendererV2(props) {
  const {
    workout,
    executionLock,
    saveStatusLabel,
    isPastWorkoutOccurrenceLocked,
    isWorkoutTitleEditing,
    workoutTitleInputRef,
    onStartTitleEditing,
    onStopTitleEditing,
    onWorkoutNameChange,
    onWorkoutTitleKeyDown,
    onBack,
    search,
    workoutStatItems,
    muscleDistribution,
    muscleVolume,
    isMuscleDistributionExpanded,
    onMuscleDistributionExpandedChange,
    maxBlockSetCount,
    dnd,
    blocks,
    showScrollTop,
    onScrollToTop,
    showAddBlockSheet,
    onCloseAddBlockSheet,
    addBlockOptions,
  } = props;

  return (
    <BuilderShell>
      <header className="lz-builder-header">
        <button
          type="button"
          onClick={onBack}
          className="lz-builder-header__button"
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="lz-builder-header__identity">
          {isWorkoutTitleEditing ? (
            <input
              ref={workoutTitleInputRef}
              type="text"
              value={workout.name}
              disabled={isPastWorkoutOccurrenceLocked}
              onChange={(event) => onWorkoutNameChange(event.target.value)}
              onBlur={onStopTitleEditing}
              onKeyDown={onWorkoutTitleKeyDown}
              placeholder="Workout Name"
              aria-label="Workout name"
              className="lz-builder-header__title-input"
            />
          ) : (
            <h1 className="lz-builder-header__title">{workout.name}</h1>
          )}
          <div className="lz-builder-header__meta">
            <SaveStatusPill label={saveStatusLabel} />
          </div>
        </div>

        {isWorkoutTitleEditing ? (
          <span />
        ) : (
          <button
            type="button"
            disabled={isPastWorkoutOccurrenceLocked}
            onClick={onStartTitleEditing}
            className="lz-builder-header__button"
            aria-label="Edit workout title"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 20h4l10-10-4-4L4 16v4zM14.5 5.5l4 4" />
            </svg>
          </button>
        )}
      </header>

      <main className="lz-builder-page">
        {isPastWorkoutOccurrenceLocked ? (
          <div className="lz-builder-alert lz-builder-alert--info">
            <p className="lz-builder-alert__title">
              {executionLock ? "Workout open in Train" : "Past workout locked on active cycle"}
            </p>
            <p className="lz-builder-alert__body">
              {executionLock
                ? "Open in Train — finish or discard to edit."
                : <>This occurrence is in the past, so you can review it here but you can&apos;t edit it.</>}
            </p>
          </div>
        ) : null}

        {/* The search dock is a block-flow child of the page rather than a cell
            of `.lz-builder-page__stack`: a grid item is stuck inside its own
            grid area, which for an auto-sized row leaves it no travel at all.
            Same panel, same props, same seam -- only the ancestor changed. */}
        <div className="lz-builder-search-dock">
          <ExerciseSearchPanel
            {...search}
            className="lz-builder-workout-search"
          />
        </div>

        <div className="lz-builder-page__stack">
          <div className="lz-product-metrics lz-product-metrics--section lz-product-metrics--aligned">
            {workoutStatItems.map((item) => (
              <MetricTile
                key={item.label}
                label={item.label}
                value={item.value}
                barValue={Number.parseFloat(item.width) || 0}
                barMax={100}
              />
            ))}
          </div>

          <MuscleDistributionPanel
            title="Muscle Distribution"
            items={muscleDistribution}
            volume={muscleVolume}
            expanded={isMuscleDistributionExpanded}
            onExpandedChange={onMuscleDistributionExpandedChange}
            bodyId="workout-muscle-distribution"
          />

          <div className="lz-builder-blocks">
            {workout.blocks.length === 0 ? (
              <EmptyState
                title="No exercise blocks yet"
                body="Use the search above to add the first exercise."
              />
            ) : null}

            <DndContext
              sensors={dnd.sensors}
              collisionDetection={closestCenter}
              onDragStart={dnd.onDragStart}
              onDragCancel={dnd.onDragCancel}
              onDragEnd={dnd.onDragEnd}
            >
              <SortableContext
                items={dnd.sortableBlockIds}
                strategy={verticalListSortingStrategy}
              >
                {workout.blocks.map((block, blockIndex) => {
                  const blockUiKey = getBlockUiKey(workout.id, block, blockIndex);
                  const isCollapsed = blocks.openBlockId !== blockUiKey;
                  const isActiveDraggedBlock = dnd.activeSortableId === blockUiKey;

                  // Unsupported/advanced blocks stay honest passthrough content:
                  // visible, never editable, never sortable, never fabricated
                  // into a supported shape (blueprint D09).
                  if (!isSortableBlockType(block.type)) {
                    return <UnsupportedBlockCard key={blockUiKey} />;
                  }

                  if (block.type === "superset") {
                    const isIncompleteSuperset = block.exercises.some(
                      (exercise) => !String(exercise.exerciseId || "").trim()
                    );

                    return (
                      <SortableBlock
                        key={blockUiKey}
                        sortableId={blockUiKey}
                        disabled={isPastWorkoutOccurrenceLocked}
                      >
                        {(sortable) => (
                          <BlockEditorSuperset
                            block={block}
                            blockUiKey={blockUiKey}
                            isDragging={sortable.isDragging || isActiveDraggedBlock}
                            disabled={isPastWorkoutOccurrenceLocked}
                            isIncompleteSuperset={isIncompleteSuperset}
                            maxSetCount={maxBlockSetCount}
                            activeSearchTarget={search.activeSearchTarget}
                            openLaneIndex={
                              blocks.openSupersetExerciseByBlock[blockUiKey]
                            }
                            sortable={sortable}
                            tempoValueForLane={(exerciseIndex, exercise) =>
                              blocks.tempoValueFor(block.id, exercise.tempo, exerciseIndex)
                            }
                            repDraftsForLaneSet={(exerciseIndex, setIndex) =>
                              blocks.repDraftsFor(block.id, setIndex, exerciseIndex)
                            }
                            prescriptionForSet={blocks.prescriptionForSet}
                            onToggleLane={(exerciseIndex) =>
                              blocks.onToggleSupersetExercise(blockUiKey, exerciseIndex)
                            }
                            onDelete={() => blocks.onRemoveBlock(block.id)}
                            onDuplicate={() => blocks.onDuplicateBlock(block.id)}
                            onRemoveLaneExercise={(exerciseIndex) =>
                              blocks.onRemoveLaneExercise(block.id, exerciseIndex)
                            }
                            onSelectLaneExercise={blocks.onSelectLaneExercise}
                            onLaneTempoFocus={(exerciseIndex, exercise) =>
                              blocks.onTempoFocus(block.id, exercise.tempo, exerciseIndex)
                            }
                            onLaneTempoChange={(exerciseIndex, value) =>
                              blocks.onTempoChange(block.id, value, exerciseIndex)
                            }
                            onLaneTempoBlur={(exerciseIndex, exercise) =>
                              blocks.onTempoBlur(block.id, exercise.tempo, exerciseIndex)
                            }
                            onLaneNotesChange={(exerciseIndex, value) =>
                              blocks.onLaneNotesChange(block.id, exerciseIndex, value)
                            }
                            onCycleRir={(exerciseIndex, setIndex, currentRir) =>
                              blocks.onCycleRir(block.id, setIndex, currentRir, exerciseIndex)
                            }
                            onRepFieldFocus={(exerciseIndex, setIndex, set, field) =>
                              blocks.onRepFieldFocus(
                                block.id,
                                setIndex,
                                set,
                                field,
                                exerciseIndex
                              )
                            }
                            onRepFieldChange={(exerciseIndex, setIndex, field, value) =>
                              blocks.onRepFieldChange(
                                block.id,
                                setIndex,
                                field,
                                value,
                                exerciseIndex
                              )
                            }
                            onRepFieldBlur={(exerciseIndex, setIndex, set, field) =>
                              blocks.onRepFieldBlur(
                                block.id,
                                setIndex,
                                set,
                                field,
                                exerciseIndex
                              )
                            }
                            onRoundsChange={(nextCount) =>
                              blocks.onRoundsChange(block.id, nextCount)
                            }
                            onRestStep={(direction) =>
                              blocks.onRestStep(block.id, block.rest, direction)
                            }
                          />
                        )}
                      </SortableBlock>
                    );
                  }

                  if (block.type === "cardio") {
                    const cardioPrescription = blocks.cardioPrescriptionFor(block);
                    const heartRateMode =
                      cardioPrescription.heartRateTargetMode || "none";
                    const usedMachineSettingKeys = blocks.usedMachineSettingKeysFor(
                      cardioPrescription.machineSettings
                    );
                    const canAddMachineSetting =
                      cardioPrescription.machineSettings.length < 2 &&
                      blocks.availableMachineSettingOptionsFor(
                        block,
                        cardioPrescription.machineSettings
                      ).length > 0;

                    return (
                      <SortableBlock
                        key={blockUiKey}
                        sortableId={blockUiKey}
                        disabled={isPastWorkoutOccurrenceLocked}
                      >
                        {(sortable) => (
                          <BlockEditorCardio
                            block={block}
                            blockUiKey={blockUiKey}
                            exerciseName={blocks.cardioExerciseNameFor(block)}
                            prescription={cardioPrescription}
                            heartRateMode={heartRateMode}
                            heartRateModeOptions={blocks.cardioHeartRateModeOptions}
                            heartRateValue={blocks.cardioZoneValueFor(cardioPrescription)}
                            machineSettingOptionsFor={(key) =>
                              blocks.machineSettingOptionsFor(block, key)
                            }
                            machineSettingLabelFor={(key) =>
                              blocks.machineSettingLabelFor(block, key)
                            }
                            usedMachineSettingKeys={usedMachineSettingKeys}
                            canAddMachineSetting={canAddMachineSetting}
                            isCollapsed={isCollapsed}
                            isDragging={sortable.isDragging || isActiveDraggedBlock}
                            disabled={isPastWorkoutOccurrenceLocked}
                            sortable={sortable}
                            onToggle={() => blocks.onToggleBlock(blockUiKey)}
                            onDelete={() => blocks.onRemoveBlock(block.id)}
                            onDuplicate={() => blocks.onDuplicateBlock(block.id)}
                            onDurationChange={(value) =>
                              blocks.onCardioDurationChange(block, value)
                            }
                            onHeartRateModeChange={(mode) =>
                              blocks.onCardioHeartRateModeChange(block, mode)
                            }
                            onHeartRateValueChange={(value) =>
                              blocks.onCardioHeartRateValueChange(block, value)
                            }
                            onStepHeartRate={(direction) =>
                              blocks.onStepCardioHeartRate(block, heartRateMode, direction)
                            }
                            onAddMachineSetting={() => blocks.onAddMachineSetting(block)}
                            onUpdateMachineSetting={(settingIndex, updates) =>
                              blocks.onUpdateMachineSetting(block, settingIndex, updates)
                            }
                            onRemoveMachineSetting={(settingIndex) =>
                              blocks.onRemoveMachineSetting(block, settingIndex)
                            }
                            onNotesChange={(value) =>
                              blocks.onCardioNotesChange(block, value)
                            }
                          />
                        )}
                      </SortableBlock>
                    );
                  }

                  return (
                    <SortableBlock
                      key={blockUiKey}
                      sortableId={blockUiKey}
                      disabled={isPastWorkoutOccurrenceLocked}
                    >
                      {(sortable) => (
                        <BlockEditorSingle
                          block={block}
                          blockUiKey={blockUiKey}
                          isCollapsed={isCollapsed}
                          isDragging={sortable.isDragging || isActiveDraggedBlock}
                          disabled={isPastWorkoutOccurrenceLocked}
                          canAddSet={block.sets.length < maxBlockSetCount}
                          canRemoveSet={block.sets.length > 1}
                          maxSetCount={maxBlockSetCount}
                          tempoValue={blocks.tempoValueFor(block.id, block.tempo)}
                          repDraftsForSet={(setIndex) =>
                            blocks.repDraftsFor(block.id, setIndex)
                          }
                          prescriptionForSet={blocks.prescriptionForSet}
                          sortable={sortable}
                          onToggle={() => blocks.onToggleBlock(blockUiKey)}
                          onDelete={() => blocks.onRemoveBlock(block.id)}
                          onDuplicate={() => blocks.onDuplicateBlock(block.id)}
                          onConvertToSuperset={() =>
                            blocks.onConvertToSuperset(block.id)
                          }
                          onTempoFocus={() => blocks.onTempoFocus(block.id, block.tempo)}
                          onTempoChange={(value) =>
                            blocks.onTempoChange(block.id, value)
                          }
                          onTempoBlur={() => blocks.onTempoBlur(block.id, block.tempo)}
                          onRestStep={(direction) =>
                            blocks.onRestStep(block.id, block.rest, direction)
                          }
                          onAddSet={() => blocks.onAddSet(block.id)}
                          onRemoveSet={(setIndex) =>
                            blocks.onRemoveSet(block.id, setIndex)
                          }
                          onCycleRir={(setIndex, currentRir) =>
                            blocks.onCycleRir(block.id, setIndex, currentRir)
                          }
                          onRepFieldFocus={(setIndex, set, field) =>
                            blocks.onRepFieldFocus(block.id, setIndex, set, field)
                          }
                          onRepFieldChange={(setIndex, field, value) =>
                            blocks.onRepFieldChange(block.id, setIndex, field, value)
                          }
                          onRepFieldBlur={(setIndex, set, field) =>
                            blocks.onRepFieldBlur(block.id, setIndex, set, field)
                          }
                          onNotesChange={(value) =>
                            blocks.onBlockNotesChange(block.id, value)
                          }
                        />
                      )}
                    </SortableBlock>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </main>

      {showScrollTop ? (
        <button
          type="button"
          onClick={onScrollToTop}
          className="lz-builder-scroll-top"
          aria-label="Back to top"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      ) : null}

      <Sheet
        open={showAddBlockSheet}
        onClose={onCloseAddBlockSheet}
        title="Add Exercise Block"
      >
        <div className="lz-builder-add-block">
          {addBlockOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={option.disabled}
              className="lz-builder-add-block__option"
            >
              <span className="lz-builder-add-block__title">{option.label}</span>
              <span className="lz-builder-add-block__help">{option.help}</span>
            </button>
          ))}
        </div>
      </Sheet>
    </BuilderShell>
  );
}
